/**
 * A golf COURSE leg is STORED as "<hub> EM-DASH ARROW <course>" and that exact
 * byte sequence is LOAD-BEARING. (The other shape in the grid, 3 AIRPORT legs —
 * one per hub — is stored as "<hub> EM-DASH <route name>": its name carries no
 * leading arrow, though two of the three carry one internally.
 * Everything below handles both; the arrow belongs to the route NAME, not to the
 * boundary.) Two consumers key on the course form:
 *   - the `golf_courses_public` view filters `location_label` on the separator
 *     and the arrow prefix (core/migrations.ts)
 *   - `listGolfHubs` recovers the hub name by splitting on it (data/rates-repo.ts)
 * plus fixtures and specs that assert the label byte-identically.
 *
 * So the stored label is never rewritten. `formatRouteLabel` collapses the
 * em-dash + arrow PAIR to a single arrow for DISPLAY only, and `displayRouteLabel`
 * (T660 M6) goes one step further by also rendering the BARE hub boundary as that
 * arrow — but ONLY where the caller can already prove the value is a composed
 * route label (three gated sites; see its own header). The two are separate
 * because `formatRouteLabel`'s pass-through of a bare boundary is a contract the
 * customer sink depends on, AND because a bare boundary is not proof of anything:
 * an operator can type one. `containsHubBoundary` is now on the two rate-label
 * WRITERS to stop new ones (T660 M6a).
 *
 * T608 M6a added the DECOMPOSITION (`splitRouteLabel`) and its inverse
 * (`composeRouteLabel`) here, beside the separator they have to agree with, so a
 * golf journey can be recorded as its two real ends. Anything that needs the hub
 * or the course out of a label calls the splitter, and anything that only needs
 * to ask whether a string CARRIES the boundary calls `containsHubBoundary` — the
 * SPLIT direction has no local copies left (`listGolfHubs` was the last, and now
 * calls the splitter).
 *
 * The COMPOSE direction still has three hand-typed compositions, deliberately
 * NOT routed through `composeRouteLabel` (each carries a keep-in-sync comment
 * pointing here):
 *   - shared/pricing-flatten.ts (the seeded grid's `location_label`)
 *   - core/data/audit-repo.ts `entryKeys` (an operator-added route's id + label)
 *   - core/data/audit-repo.ts's legacy 409 copy (rendered verbatim by the kiosk)
 * Their bytes land in `rates.location_id` / `rates.location_label` and in
 * operator-facing copy, so re-routing them is a data migration, not a refactor.
 *
 * Lives in shared/ rather than client/ on purpose: scripts/check-copy.sh bans a
 * literal em dash under src/client, and matching the stored separator needs one.
 * If this ever moves into client/, switch the constants to \u escapes.
 */

/**
 * The HUB boundary. Every golf label is composed `${hubName} — ${routeName}` by
 * the two writers (shared/pricing-flatten.ts for the seeded grid,
 * core/data/audit-repo.ts's entryKeys for an operator-added route), so this —
 * not the em-dash+arrow pair — is where the label actually splits. The arrow
 * belongs to the route NAME: pricing.json names a course leg '→ Druids Glen Resort'
 * and an airport leg 'Dublin Airport ↔ Arklow Bay Hotel'.
 */
const HUB_SEPARATOR = ' — ';

/** The one arrow glyph, declared once so nothing below re-types it. */
const ARROW = '→';

/** A COURSE leg's route name begins with this; an airport leg's does not. That
 *  prefix is what `golf_courses_public` filters on (core/migrations.ts v6). */
const COURSE_PREFIX = `${ARROW} `;

/** What the database stores for a course leg: space, em dash, space, arrow,
 *  space — the hub boundary followed immediately by the course prefix. */
const STORED_SEPARATOR = `${HUB_SEPARATOR}${COURSE_PREFIX}`;

/**
 * What Helen reads: one arrow, one meaning.
 *
 * Exported for the ONE surface that has to join two ends itself rather than
 * render a label: BookingDetail's Journey line, where a golf course leg is now
 * two stop rows (T608 M6a) and pre-M6a was a single composite label that
 * `formatRouteLabel` rendered with this exact separator. Joining with it keeps
 * that screen reading like every other one — and keeps the em dash and the arrow
 * declared here, once, since scripts/check-copy.sh bans a literal em dash under
 * src/client.
 */
export const DISPLAY_SEPARATOR = ` ${ARROW} `;

/**
 * Display-safe route label. A no-op for every non-golf label, so it is safe to
 * apply wherever a location label is rendered.
 */
export function formatRouteLabel<T extends string | null | undefined>(label: T): T {
  if (typeof label !== 'string') return label;
  return label.replaceAll(STORED_SEPARATOR, DISPLAY_SEPARATOR) as T;
}

/**
 * What an OPERATOR SCREEN renders where the value's PROVENANCE is already known
 * (T660 M6): a hub boundary shown as the one arrow Helen reads at every other
 * boundary.
 *
 * ⚠️ It does NOT deliver SC-7 ("no em dash reaches an operator screen"), and no
 * longer claims to (T660 M6a). SC-7 as written is unsatisfiable by design: the
 * three surfaces that CAN prove provenance call this (see the caller contract),
 * but the rate-row surfaces — Location's route tiles, RateList's rows — cannot,
 * so the 3 seeded AIRPORT legs still read with a bare em dash there. Closing
 * that would mean rewriting bytes whose provenance nothing on those screens can
 * establish, which is how M6 corrupted an operator-typed venue name. The board
 * records SC-7 and P0-18 as unsatisfiable-by-design and unamended.
 *
 * SEPARATE from `formatRouteLabel` on purpose, not a widening of it — and read
 * the CALLER CONTRACT below before adding a call site.
 * `formatRouteLabel` is contract-bound to pass a BARE hub boundary through
 * untouched (test/shared/route-label.spec.ts pins 'Druids Glen — clubhouse'
 * verbatim), and non-display callers depend on that: notify/customer-message.ts's
 * `customerText` layers on top of it and needs the bare em dash to still be there
 * so its own layer 4 can degrade it to the ASCII hyphen the brand voice wants —
 * a customer must never read an arrow-joined composite. So the two renderings are
 * genuinely different sinks, and the operator one is expressed HERE, once, rather
 * than at 13 call sites.
 *
 * `scripts/check-copy.sh` bans a literal em dash under src/client AND src/shared,
 * exempting per FILE per RULE (this file and pricing-flatten.ts are the two
 * shared exemptions for that rule), so no CLIENT call site can re-type the byte
 * being replaced. That is a rule about where the byte may be TYPED — it is NOT a
 * guarantee about the strings that arrive here. `src/engine` is not a scan root
 * at all, and engine/quote.ts composes several line-item labels with an em dash
 * of its own, one of them around operator-typed free text (see the CALLER
 * CONTRACT below). A value's provenance, never the gate, is what makes a call
 * site safe.
 *
 * The gap it closes: `formatRouteLabel` rewrites only the em-dash + arrow PAIR,
 * so an 18-of-21 course leg already rendered with an arrow while the 3 seeded
 * AIRPORT legs (one per hub — 'Arklow Bay Hotel — Dublin Airport ↔ Arklow Bay
 * Hotel') kept the bare em dash on every operator surface.
 *
 * Composed from the existing constants, and the ORDER is load-bearing: the pair
 * must collapse FIRST, or its own em dash becomes a second arrow
 * ('Hub — → Course' -> 'Hub → → Course').
 *
 * ⛔ CALLER CONTRACT — it is NOT safe wherever a location label is rendered, and
 * that is the one way it differs from `formatRouteLabel` in practice (T660 M6
 * R1, live-reproduced in review). It collapses EVERY ' — ', including one a
 * customer or Helen typed: 'The K Club — Straffan' comes back as
 * 'The K Club → Straffan'. No byte test can tell the two apart — a typed 'X — Y'
 * and a stored route label are the same shape, and `splitRouteLabel` reads both
 * as a clean pair — so the CALLER must already know its value is a stored golf
 * ROUTE label before calling. Callers that render a value which can fall through
 * to free text (anything reading `pickupSearchText` / `pickupAddressLine` /
 * `dropoffSearchText`; a `booking_stops.address`, which is free text on ANY
 * booking_type including golf — `journeyStops` writes `role: 'middle'` rows from
 * `customerStops`/`operatorStops` on every service; or a `quote.lines[].label`,
 * which the ENGINE composes on every path but the grid price) must gate on
 * PROVENANCE instead: `store/selectors.ts`'s `pickupEndDisplay` is that gate on
 * the live job, BookingDetail gates per STOP, collapsing only an address that
 * re-derives the persisted quote's rate key, and Quote's `displayLineLabel`
 * gates per LINE ITEM. Everything else
 * takes `formatRouteLabel`, which only ever touches the em-dash + arrow PAIR and
 * so cannot corrupt a lone dash — the invariant
 * test/shared/route-label.spec.ts pins ('Druids Glen — clubhouse' verbatim).
 *
 * There are NO ungated callers left (T660 M6a). M6 shipped five — Location's
 * route tiles and RateList's four sites (three renders + the search-filter
 * haystack) — on the reasoning that reading the rate ROW proves the value is a
 * composed label. That reasoning was FALSE and the gate proved it twice:
 * `rates.location_label` was validated length-only at the writer, so a venue
 * Helen typed as 'Druids Glen — clubhouse' WAS the column, and an operator-added
 * COURSE name carrying its own dash produced a composite that re-derives its own
 * rate key (`slugify` flattens the arrow and the em dash to one '-' run) and so
 * passed even the provenance gates. All five are back on `formatRouteLabel`, and
 * the premise is now made true where it can be — at the WRITER: server/app.ts's
 * `parseRateEntryBody` and core/data/audit-repo.ts's `insertGolfRoute` both
 * REFUSE a name or hub carrying the boundary (via `containsHubBoundary` below).
 * A row written before that refusal keeps its own bytes, which is why the render
 * stayed narrow rather than trusting the new rule retroactively.
 *
 * Quote's rate LINE ITEM was never one of them — that claim shipped in
 * M6 R1 and was false. `quote.lines[].label` is engine-composed on every path
 * but the grid price, with an ordinary sentence separator
 * ('Airport transfer · bray', engine/quote.ts:109 — ' · ' since T1723 M4,
 * II-flow-F4; an em dash before it), and the estimate arm
 * interpolates `estimate.anchoredPlace`, which is operator-typed free text
 * ('Estimated airport transfer · The K Club — Straffan', :50), so a boundary
 * byte still reaches this value from the OPERATOR even though the engine's own
 * separator no longer carries one. So it gates on
 * provenance like the two gates named above (`displayLineLabel`,
 * screens/Quote.tsx: a PRICED GOLF base line only, which is the one branch that
 * reads `rates.location_label`). Adding a call site means answering that same
 * question first: what proves this value came from the rate row?
 *
 * DISPLAY ONLY, exactly like `formatRouteLabel`: never call it on a value bound
 * for `rates.location_label`, `booking_stops.address`, a rate-key lookup or a
 * write payload. The stored bytes are load-bearing (the `golf_courses_public`
 * view filters on them; `listGolfHubs` splits on them) and are unchanged here.
 */
export function displayRouteLabel<T extends string | null | undefined>(label: T): T {
  if (typeof label !== 'string') return label;
  return formatRouteLabel(label).replaceAll(HUB_SEPARATOR, DISPLAY_SEPARATOR) as T;
}

/**
 * Does this string carry the hub boundary anywhere in it? (T608 M6a, security
 * review.)
 *
 * The BYTE question, deliberately separate from the semantic one
 * `splitRouteLabel` answers. A caller that must know "is it safe to put this
 * string on ONE side of a boundary I am about to compose?" cannot ask the
 * splitter: the splitter returns null both for "no boundary here" and for
 * "boundary present but a half is empty", so a trailing-separator end
 * ('Arklow Bay Hotel — ') reads as null and would be composed into a
 * TWO-boundary label that `splitRouteLabel` cannot take back apart. Exported so
 * that question is asked here, beside the separator, and never by re-typing the
 * em dash at the call site (`scripts/check-copy.sh` bans one under src/client).
 *
 * T660 M6a put it on the two WRITE paths as well, which is where it earns the
 * most: `parseRateEntryBody` (src/server/app.ts — the shared parser behind
 * /api/rate-entry, /api/rate-cell and /api/promote) and `insertGolfRoute`
 * (core/data/audit-repo.ts). Both REFUSE, never normalise. That is what makes
 * "a hub boundary in `rates.location_label` is the composer's own" a fact about
 * newly written rows rather than an assumption three display gates were resting
 * on — see `displayRouteLabel`'s header for what was corrupted while it was only
 * an assumption.
 */
export function containsHubBoundary(value: string): boolean {
  return value.includes(HUB_SEPARATOR);
}

/** The two ENDS a golf route label names: where the party starts and where the
 *  bus takes them. Both are ordinary place labels, never a composite. */
export type RouteEnds = {
  origin: string;
  destination: string;
  /**
   * The LEG SHAPE: true when the route name began with the arrow — a COURSE leg
   * ('→ Druids Glen Resort'). false for an AIRPORT leg, whose name already names both
   * its own ends and carries no prefix.
   *
   * MEASURE that second set with this field's own predicate — a route name that
   * does not start with the arrow — never by searching for the ↔ glyph. In
   * docs/pricing.json the predicate finds 3 of the 21 seeded golf routes, exactly
   * one per hub: 'Dublin Airport ↔ Arklow Bay Hotel', 'Dublin Airport → Druids
   * Glen Resort' and 'Dublin Airport → Woodenbridge Hotel'. Searching for ↔ finds only
   * the first, because the other two use the same arrow glyph as a course leg and
   * differ only in where it sits. Both shapes are real and live.
   *
   * Read off the label, never guessed. It is the one bit of the stored form that
   * the two place labels do NOT hold — a course name and an arrow-less airport
   * route name are both just text — so without it the composer below cannot be
   * an inverse: it would re-add an arrow to an airport leg.
   *
   * It is also a CONSUMER GATE, not only bookkeeping: `journeyStops`
   * (client/lib/actions.ts) records the two ends as stops for a course leg ONLY,
   * because an airport leg has no course to be the far end.
   */
  courseLeg: boolean;
};

/**
 * Decompose a stored golf route label into its two ends (T608 M6a) — the
 * inverse of the composer below, and the ONE splitter.
 *
 * There were already three expressions of this decomposition: the
 * `golf_courses_public` view (split_part on the hub boundary, then strip
 * `^→\s*`), `listGolfHubs`' hub recovery (indexOf the hub boundary, take the
 * prefix) and `formatRouteLabel` above. This is deliberately the SAME operation
 * as the first two rather than a fourth variant, so the values it returns are
 * byte-identical to labels those readers already produce: `origin` is exactly
 * the hub name `listGolfHubs` recovers, and for a COURSE leg `destination` is
 * exactly the course name `golf_courses_public` publishes to the website's
 * course dropdown (an airport leg is filtered out of that view entirely and its
 * name passes through whole). That is what makes both ends ordinary queryable
 * place labels.
 *
 * null when the label names no pair — a non-golf label, an empty half, or a
 * legacy/hand-typed label with no hub boundary. Callers must degrade to their
 * single-end behaviour on null rather than invent an end.
 */
export function splitRouteLabel(label: string | null | undefined): RouteEnds | null {
  if (typeof label !== 'string') return null;
  // FIRST occurrence, and > 0 — matches listGolfHubs' `sep > 0` byte for byte,
  // so a label that opens with the separator has no origin and no pair.
  const at = label.indexOf(HUB_SEPARATOR);
  if (at <= 0) return null;
  const origin = label.slice(0, at).trim();
  const rest = label.slice(at + HUB_SEPARATOR.length).trim();
  // The arrow marks a COURSE leg and is not part of the place's name; the view's
  // `regexp_replace(..., '^→\s*', '')` strips exactly this. An airport leg has
  // no prefix and passes through whole (its internal ↔ is the rate's own name,
  // which this milestone must not rewrite). Which of the two it was is the one
  // thing the ends alone cannot recover, so it is RETURNED, not discarded.
  const courseLeg = rest.startsWith(ARROW);
  const destination = (courseLeg ? rest.slice(ARROW.length) : rest).trim();
  if (origin === '' || destination === '') return null;
  return { origin, destination, courseLeg };
}

/**
 * The GOLF ROUTE TILE's label (T660 M11, P2-2). Ruled by Alex 2026-08-02:
 * DISPLAY-ONLY, COURSE LEGS ONLY. `rates.location_label` is never touched.
 *
 * Why the tiles restate at all: Location.tsx shows the chosen hub directly
 * above the list in `.loc-selected-label`, then every tile leads with that
 * same hub name again. 18 of the 21 seeded routes are course legs (Arklow 7,
 * Druids Glen 6, Woodenbridge 5), so this is the bulk of the list.
 *
 * ⛔ THE GATE IS `courseLeg`, NEVER A GLYPH SEARCH, and that is the whole
 * safety argument. A course leg's route NAME begins with the arrow, which is
 * exactly what `golf_courses_public` filters on. An AIRPORT leg's does not -
 * it begins with the place ("Dublin Airport ..."). Searching for `↔` to spot
 * an airport leg looks equivalent and is not: of the three seeded airport
 * legs only ONE uses `↔` (Arklow's); the other two use `→` internally
 * (`Dublin Airport → Druids Glen Resort`, `Dublin Airport → Woodenbridge
 * Hotel`), so a glyph search catches 1 of 3 and silently mangles the other
 * two. Verified against the seeded rates table, not assumed.
 *
 * ⛔ AND `courseLeg === false` IS WHAT KEEPS THIS OUT OF M6'S BURNED GROUND.
 * M6 shipped `displayRouteLabel` at this very call site on the premise that
 * reading the rate ROW proves the value is a composed label; the gate proved
 * that FALSE twice, because a venue Helen typed as 'Druids Glen — clubhouse'
 * IS the column (see the revert record above). That row splits, but its rest
 * does not start with the arrow, so `courseLeg` is false and it is returned
 * UNCHANGED. The premise is now also true at the WRITER (`containsHubBoundary`
 * refuses the boundary since M6a) - but rows written before that refusal keep
 * their own bytes, which is precisely the case this branch protects.
 * Legacy rows are NOT backfilled or normalised here (Alex, same ruling):
 * they are left alone and reported.
 *
 * Everything else - a non-golf label, a label with no hub boundary, an empty
 * half, an airport leg - degrades to `formatRouteLabel`, i.e. exactly what
 * this call site rendered before.
 */
export function routeTileLabel<T extends string | null | undefined>(label: T): T {
  if (typeof label !== 'string') return label;
  const ends = splitRouteLabel(label);
  if (ends === null || !ends.courseLeg) return formatRouteLabel(label);
  return ends.destination as T;
}

/**
 * Recompose a route label from the ends `splitRouteLabel` returned — its exact
 * inverse, both ways round and for BOTH leg shapes:
 *   compose(split(L)) === L   for every label the WRITERS compose
 *   split(compose(e))  === e   for every RouteEnds `e`
 *
 * The first identity is stated over the WRITERS' labels, not over everything
 * `split` accepts, and that is measured rather than hedged: the splitter trims
 * and normalises while the composer does not re-add what it dropped, so a
 * hand-typed label round-trips to the NORMALISED form —
 * 'Hub — →Course' and 'Hub — →   Course' both return as 'Hub — → Course', and
 * 'Hub —  Course' (two spaces) as 'Hub — Course'. No END is ever lost, but the
 * bytes can move, which is one more reason nothing here rewrites a STORED label.
 *
 * It takes the whole `RouteEnds` rather than two strings precisely BECAUSE of
 * that. The leg shape is not recoverable from the two place labels, so a
 * signature a caller could supply from ends alone cannot be an inverse: it would
 * re-add a course arrow to one of the 3 arrow-less airport legs (one per hub —
 * see `RouteEnds.courseLeg` for how that set is measured). Passing the splitter's
 * own result back makes the identity a property of the type.
 *
 * The bytes it emits are exactly what the two label WRITERS store
 * (`${hubName} — ${routeName}` in shared/pricing-flatten.ts and audit-repo.ts's
 * entryKeys): a course leg gets the em-dash + arrow pair, which `formatRouteLabel`
 * renders as one arrow; an airport leg gets the hub boundary alone, which
 * `formatRouteLabel` leaves untouched.
 *
 * A DISPLAY value only. It is never a rate key (`slugify` collapses the `__`
 * boundary, so a whole label cannot be slugified) and it never rewrites
 * `rates.location_label`.
 */
export function composeRouteLabel(ends: RouteEnds): string {
  const separator = ends.courseLeg ? STORED_SEPARATOR : HUB_SEPARATOR;
  return `${ends.origin}${separator}${ends.destination}`;
}
